import { FunctionComponent, useMemo, useState } from 'react';

import { AnalyticEventAction } from 'analytics';
import { trackActionButtonAnalyticsEvent } from 'analytics/events/action-button';

import { observer } from 'mobx-react';

import { overdueReportTicketSelectors } from 'tests/models/components/ticket/overdue-report-ticket/overdue-report-ticket.selectors';

import useNetworkLoading from 'mobx/hooks/useNetworkLoading';

import { useStores } from 'mobx/hooks/useStores';

import { fromNow } from 'utils/DateUtils';

import { API_LABELS } from 'constants/apiUrls';

import Patient from 'models/Patient';

import { useToggleCallLogWithNav } from 'hooks/useToggleCallLog';

import { useTicketActionCallbacks } from 'components/Ticket/TicketsContainers/useTicketActionCallbacks';
import { OutlinedSubtextButton } from 'components/UIkit/atoms/Button';

interface Props {
  patient: Patient;
  toggleSmsModal: (shouldOpenSmsBlockedPopup: boolean, patient: Patient) => void;
  ticketIndex: number;
  ticketSectionCurrentPage: number;
}

const OverdueActions: FunctionComponent<Props> = ({
  patient,
  ticketIndex,
  ticketSectionCurrentPage = 0
}) => {
  const { patientPageStore } = useStores();
  const isFetchingOverdueSection = useNetworkLoading([
    API_LABELS.WQ_FETCH_OVERDUE_SECTION(patient.id)
  ]);
  const [isRequestingSnooze, setIsRequestingSnooze] = useState(false);
  const handleLogCallClicked = useToggleCallLogWithNav(patient.id);
  const { onSnooze } = useTicketActionCallbacks();

  const placeCallCaption = useMemo(() => {
    if (!patient.hasPersonalRequestSent) {
      return;
    }
    if (!patient.hasPatientCalledSinceNeededToReport) {
      if (!patient.lastReportRequestDate) {
        return;
      }
      return `Reminded ${fromNow(patient.lastReportRequestDate)}`;
    }
    if (!patient.lastCall?.createdAt) {
      return;
    }
    return `Called ${fromNow(new Date(patient.lastCall.createdAt))}`;
  }, [
    patient.hasPatientCalledSinceNeededToReport,
    patient.hasPersonalRequestSent,
    patient.lastCall?.createdAt,
    patient.lastReportRequestDate
  ]);

  const handleSnooze = async () => {
    trackActionButtonAnalyticsEvent({
      action: AnalyticEventAction.Snooze,
      value: 'single report',
      patient_id: patient.id,
      item_index: ticketIndex + 1,
      page_number: ticketSectionCurrentPage + 1
    });
    setIsRequestingSnooze(true);
    try {
      await patientPageStore!.snooze(patient.id);
      onSnooze && onSnooze(patient);
    } finally {
      setIsRequestingSnooze(false);
    }
  };

  const isSnoozeButtonDisabled = isRequestingSnooze || isFetchingOverdueSection;

  const isPatientDrugSpecificAndHospitalized =
    patient.hasDrugSpecificProtocol && patient.isOverdueHospitalized;

  const shouldShowSnooze =
    !patient.hasDrugSpecificHourlyProtocol && !isPatientDrugSpecificAndHospitalized;

  const shouldShowLogCall = !isPatientDrugSpecificAndHospitalized;

  return (
    <div className="d-flex">
      {shouldShowSnooze && (
        <OutlinedSubtextButton
          onClick={(event) => {
            event.stopPropagation();
            handleSnooze();
          }}
          subText="Until Next Report"
          disabled={isSnoozeButtonDisabled}
          mr={8}
          testHook={overdueReportTicketSelectors.snoozeButton(patient.id)}
        >
          Snooze
        </OutlinedSubtextButton>
      )}

      {shouldShowLogCall && (
        <OutlinedSubtextButton
          onClick={() => {
            trackActionButtonAnalyticsEvent({
              action: AnalyticEventAction.LogCall,
              value: 'single report',
              patient_id: patient.id,
              item_index: ticketIndex + 1,
              page_number: ticketSectionCurrentPage + 1
            });
            handleLogCallClicked();
          }}
          subText={placeCallCaption}
          testHook="overdueLogCallButton"
        >
          Log Call
        </OutlinedSubtextButton>
      )}
    </div>
  );
};

export default observer(OverdueActions);
